<template>
  <component :is="layoutComponent" page-title="Tank Watch" page-subtitle="Live 500 frac, 400 upright, and Sand Commander">
    <div class="pt-4 space-y-4">
      <div class="toolbar">
        <select v-if="locations.length" v-model="locationId" class="loc" @change="refresh">
          <option v-for="loc in locations" :key="loc.id" :value="String(loc.id)">{{ loc.name }}</option>
        </select>
        <button class="btn" type="button" :disabled="loading" @click="refresh">Refresh</button>
      </div>
      <p class="legend">HIGH · HAUL OFF · EMPTY · IN USE</p>
      <p v-if="error" class="err">{{ error }}</p>

      <div v-for="section in tankSections" :key="section.id" class="watch-section">
        <h3>{{ section.label }}</h3>
        <div class="scroller" :class="{ wide: section.style === 'commander' }">
          <template v-if="section.style === 'commander'">
            <article v-for="unit in groupCommanderUnits(section.tanks)" :key="`sc-${unit.id}`" class="commander-watch">
              <SandCommanderVisual
                :name="unit.name"
                :bays="commanderVisualBays(unit)"
                :capacity-bbl="110"
                :fill-token="unit.bays.map((bay: any) => bay.measuredAt || bay.id).join('-')"
                :width-px="520"
                @select="(i: number) => unit.bays[i]?.id && onTap(unit.bays[i])"
              />
            </article>
          </template>
          <TankWatchCard
            v-else
            v-for="tank in section.tanks"
            :key="tank.id"
            :tank="tank"
            :status="normalizeWatchStatus(tank.watchStatus || tank.watch_status || tank.flag)"
            @tap="onTap(tank)"
            @longpress="openSheet(tank)"
          />
          <p v-if="!section.tanks.length && !loading" class="empty">No {{ section.shortLabel.toLowerCase() }} tanks here.</p>
        </div>
      </div>
    </div>

    <div v-if="sheetTank" class="backdrop" @click.self="sheetTank = null">
      <div class="modal">
        <h2>{{ sheetTank.name }}</h2>
        <p class="hint">Hold a card to flag it. Tap to {{ canLoad ? 'log a load' : 'open the gauge' }}.</p>
        <div class="flag-grid">
          <button
            v-for="f in WATCH_FLAGS"
            :key="f.status"
            type="button"
            class="flag-btn"
            :style="{ borderColor: f.color, color: f.color }"
            @click="setFlag(sheetTank, f.status)"
          >{{ f.pill }}</button>
          <button type="button" class="flag-btn" @click="setFlag(sheetTank, 'none')">CLEAR FLAG</button>
        </div>
        <button v-if="canNotify" class="btn primary" type="button" @click="openNotify(sheetTank)">Notify driver</button>
        <button v-if="canLoad" class="btn primary" type="button" @click="openLoad(sheetTank)">Log load</button>
      </div>
    </div>

    <div v-if="notifyTank" class="backdrop" @click.self="notifyTank = null">
      <form class="modal" @submit.prevent="sendCall">
        <h2>Notify driver · {{ notifyTank.name }}</h2>
        <label>
          <span>Call type</span>
          <select v-model="notifyForm.type" required>
            <option value="bringFresh">Bring fresh</option>
            <option value="bringProduction">Bring production</option>
            <option value="bringHeavy">Bring heavy</option>
            <option value="disposal">Take to disposal</option>
          </select>
        </label>
        <label>
          <span>Barrels (optional)</span>
          <input v-model.number="notifyForm.barrels" type="number" min="0" step="0.1" placeholder="120" />
        </label>
        <div class="actions">
          <button class="btn" type="button" @click="notifyTank = null">Cancel</button>
          <button class="btn primary" type="submit" :disabled="saving">{{ saving ? 'Sending…' : 'Send call' }}</button>
        </div>
      </form>
    </div>

    <div v-if="loadTank" class="backdrop" @click.self="loadTank = null">
      <form class="modal" @submit.prevent="saveLoad">
        <h2>{{ loadForm.action === 'push' ? 'Push-in' : 'Pulled load' }} · {{ loadTank.name }}</h2>
        <label>
          <span>Action</span>
          <select v-model="loadForm.action">
            <option value="push">Pushed in</option>
            <option value="pull">Pulled load</option>
          </select>
        </label>
        <label>
          <span>Fluid</span>
          <select v-model="loadForm.fluid">
            <option value="water">Water</option>
            <option value="oil">Oil</option>
          </select>
        </label>
        <label>
          <span>Barrels</span>
          <input v-model.number="loadForm.barrels" type="number" min="0" step="0.1" required />
        </label>
        <div class="actions">
          <button class="btn" type="button" @click="loadTank = null">Cancel</button>
          <button class="btn primary" type="submit" :disabled="saving">{{ saving ? 'Saving…' : 'Save load' }}</button>
        </div>
      </form>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import CompanyManAppLayout from '@/components/layout/CompanyManAppLayout.vue'
import DriverAppLayout from '@/components/layout/DriverAppLayout.vue'
import TankWatchCard from '@/components/tanks/TankWatchCard.vue'
import SandCommanderVisual from '@/components/tanks/SandCommanderVisual.vue'
import { TANK_KINDS, WATCH_FLAGS, commanderVisualBays, groupCommanderUnits, normalizeWatchStatus, tankCuts, tankKind } from '@/components/tanks/fracTank'

const route = useRoute()
const router = useRouter()
const userType = localStorage.getItem('user_type') || ''
const isDriver = computed(() => userType === 'driver' || route.path.startsWith('/driver'))
const isCompanyMan = computed(() => userType === 'company_man' || route.path.startsWith('/company-man'))
const layoutComponent = computed(() => {
  if (isDriver.value) return DriverAppLayout
  if (isCompanyMan.value) return CompanyManAppLayout
  return AppLayout
})
const canNotify = computed(() => !isDriver.value)
const canLoad = computed(() => isDriver.value)

const locations = ref<Array<{ id: number; name: string }>>([])
const locationId = ref('')
const tanks = ref<any[]>([])
const tankSections = computed(() =>
  TANK_KINDS.map((kind) => ({
    ...kind,
    tanks: tanks.value.filter((tank) => tankKind(tank.tankType || tank.tank_type).id === kind.id),
  })),
)
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const sheetTank = ref<any>(null)
const notifyTank = ref<any>(null)
const loadTank = ref<any>(null)
const notifyForm = reactive({ type: 'bringFresh', barrels: '' as string | number })
const loadForm = reactive({ action: 'push', fluid: 'water', barrels: 120 as string | number })

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
    'Content-Type': 'application/json',
  }
}

async function refresh() {
  loading.value = true
  error.value = ''
  try {
    const qs = locationId.value ? `?location_id=${locationId.value}` : ''
    const res = await fetch(`/api/employee/tanks${qs}`, { headers: authHeaders() })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    locations.value = data.locations || []
    if (!locationId.value) locationId.value = String(data.locationId || locations.value[0]?.id || '')
    tanks.value = data.tanks || []
  } catch (e: any) {
    error.value = e.message || 'Failed to load tanks'
  } finally {
    loading.value = false
  }
}

async function setFlag(tank: any, status: string) {
  error.value = ''
  try {
    const res = await fetch(`/api/employee/tanks/${tank.id}/flag`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ status }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    tank.watchStatus = data.watch_status
    tank.watch_status = data.watch_status
    tank.flag = data.flag
    sheetTank.value = null
  } catch (e: any) {
    error.value = e.message || 'Could not flag tank'
  }
}

function openSheet(tank: any) {
  sheetTank.value = tank
}

function openNotify(tank: any) {
  sheetTank.value = null
  notifyTank.value = tank
  notifyForm.type = 'bringFresh'
  notifyForm.barrels = ''
}

function openLoad(tank: any, preset?: { type?: string; barrels?: string }) {
  sheetTank.value = null
  loadTank.value = tank
  const type = preset?.type || ''
  loadForm.action = type === 'disposal' ? 'pull' : 'push'
  loadForm.fluid = type === 'bringFresh' || type === 'disposal' ? 'water' : (type ? 'oil' : loadForm.fluid)
  loadForm.barrels = preset?.barrels ? Number(preset.barrels) : 120
}

function onTap(tank: any) {
  if (canLoad.value) {
    openLoad(tank)
    return
  }
  if (isCompanyMan.value) return
  if (userType === 'employee' && ['supervisor_1', 'supervisor_2', 'supervisor_3'].includes(localStorage.getItem('crew_level') || '')) {
    router.push('/employee/tanks')
    return
  }
}

async function sendCall() {
  if (!notifyTank.value) return
  saving.value = true
  error.value = ''
  try {
    const barrels = notifyForm.barrels === '' ? null : Number(notifyForm.barrels)
    const res = await fetch('/api/employee/driver-calls', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        id: `call-${notifyTank.value.id}-${Date.now()}`,
        tank_id: notifyTank.value.id,
        tank_name: notifyTank.value.name,
        type: notifyForm.type,
        barrels,
        status: 'open',
        created_at: new Date().toISOString().slice(0, 23),
      }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    notifyTank.value = null
  } catch (e: any) {
    error.value = e.message || 'Could not notify driver'
  } finally {
    saving.value = false
  }
}

async function saveLoad() {
  if (!loadTank.value) return
  saving.value = true
  error.value = ''
  try {
    const res = await fetch(`/api/employee/tanks/${loadTank.value.id}/loads`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        direction: loadForm.action === 'push' ? 'in' : 'out',
        action: loadForm.action,
        fluid: loadForm.fluid,
        barrels: Number(loadForm.barrels),
        location_id: Number(locationId.value) || undefined,
        recorded_at: new Date().toISOString().slice(0, 23),
      }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    loadTank.value = null
    await refresh()
  } catch (e: any) {
    error.value = e.message || 'Could not save load'
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  await refresh()
  const tankId = Number(route.query.tank || 0)
  if (tankId && canLoad.value) {
    const tank = tanks.value.find((t) => t.id === tankId)
    if (tank) openLoad(tank, { type: String(route.query.type || ''), barrels: String(route.query.barrels || '') })
  }
})
</script>

<style scoped>
.toolbar { display: flex; gap: 10px; flex-wrap: wrap; }
.loc, .btn, select, input {
  background: #1e293b; color: #e2e8f0; border: 1px solid #334155; border-radius: 8px;
  padding: 8px 12px; font-weight: 700;
}
.btn.primary { background: rgba(16,185,129,.18); color: #5eead4; border-color: rgba(16,185,129,.4); }
.legend { color: #92A5B7; font-size: 11px; font-weight: 800; letter-spacing: .08em; }
.watch-section { margin-bottom: 18px; }
.watch-section h3 { color: #e2e8f0; font-size: 14px; font-weight: 800; margin: 0 0 8px; }
.scroller { display: flex; gap: 14px; overflow-x: auto; padding-bottom: 12px; }
.scroller.wide { display: block; }
.commander-watch { background: #17263A; border: 1px solid #2D4259; border-radius: 16px; padding: 10px; }
.err { color: #f87171; }
.empty { color: #94a3b8; text-align: center; padding: 40px 0; }
.backdrop { position: fixed; inset: 0; background: rgba(2,6,23,.7); display: grid; place-items: center; z-index: 80; }
.modal { width: min(420px, 92vw); background: #0F1B2D; border: 1px solid #334155; border-radius: 16px; padding: 20px; display: grid; gap: 12px; }
.modal h2 { color: #fff; font-size: 18px; font-weight: 800; }
.modal label { display: grid; gap: 6px; color: #94a3b8; font-size: 12px; font-weight: 700; }
.hint { color: #92A5B7; font-size: 12px; }
.flag-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.flag-btn {
  background: transparent; border: 1px solid #2D4259; color: #EAF2F8;
  border-radius: 10px; padding: 10px 8px; font-weight: 900; font-size: 12px; cursor: pointer;
}
.actions { display: flex; justify-content: flex-end; gap: 8px; }
</style>
